import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { ActionsList } from "@/components/reports/ActionsList";
import { ExecutiveSummary } from "@/components/reports/ExecutiveSummary";
import { NextTopicCard } from "@/components/reports/NextTopicCard";
import { ReportSummaryBar } from "@/components/reports/ReportSummaryBar";
import { WeeksList } from "@/components/reports/WeeksList";
import { TopPagesTable } from "@/components/analytics/TopPagesTable";
import { api, type Report } from "@/lib/api";
import { formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated } from "@/lib/pb";

export const Route = createFileRoute("/reports/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: ReportsPage,
});

function ReportsPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const indexQuery = useQuery({
    queryKey: ["reports-index"],
    queryFn: () => api.reports.list(),
  });

  // Auto-select most recent when the index loads
  useEffect(() => {
    if (!selected && indexQuery.data?.weeks?.[0]) {
      setSelected(indexQuery.data.weeks[0].week);
    }
  }, [indexQuery.data, selected]);

  const reportQuery = useQuery({
    queryKey: ["report", selected],
    queryFn: () => api.reports.get(selected!),
    enabled: !!selected,
  });

  if (indexQuery.isLoading) {
    return <Shell><Skeleton label="loading reports index" /></Shell>;
  }
  if (indexQuery.error || !indexQuery.data) {
    return (
      <Shell>
        <div className="surface p-6 font-mono text-sm text-danger">
          {(indexQuery.error as Error)?.message ?? "no reports"}
        </div>
      </Shell>
    );
  }

  const weeks = indexQuery.data.weeks;
  const report = reportQuery.data;

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
        <p className="path-chip">/reports · weekly seo intelligence</p>
        <div className="flex items-baseline gap-3 mt-1">
          <h1 className="text-3xl font-semibold">Reports</h1>
          <span className="font-mono text-sm text-muted">[{weeks.length} weeks]</span>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        <WeeksList
          weeks={weeks}
          selected={selected ?? ""}
          onSelect={setSelected}
        />

        <div className="space-y-4 min-w-0">
          {reportQuery.isLoading || !report ? (
            <Skeleton label={`loading ${selected ?? "report"}`} />
          ) : reportQuery.error ? (
            <div className="surface p-6 font-mono text-sm text-danger">
              {(reportQuery.error as Error).message}
            </div>
          ) : (
            <ReportBody report={report} />
          )}
        </div>
      </div>
    </Shell>
  );
}

function ReportBody({ report }: { report: Report }) {
  return (
    <>
      <div className="flex items-baseline justify-between">
        <div>
          <p className="font-mono text-lg text-primary">{report.week}</p>
          <p className="font-mono text-xs text-muted/60">
            generated {formatRelativeTime(report.generated_at)}
          </p>
        </div>
      </div>

      <ReportSummaryBar summary={report.summary} deltas={report.deltas} />

      {report.executive_summary && <ExecutiveSummary text={report.executive_summary} />}

      <ActionsList actions={report.actions ?? []} />

      {report.suggested_next_topic && <NextTopicCard topic={report.suggested_next_topic} />}

      {report.top_pages && report.top_pages.length > 0 && (
        <TopPagesTable rows={report.top_pages} limit={10} />
      )}
    </>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-6xl mx-auto space-y-4">{children}</div>;
}

function Skeleton({ label }: { label: string }) {
  return (
    <div className="surface p-12 text-center">
      <p className="font-mono text-sm text-muted">$ {label}…</p>
    </div>
  );
}
